import axios from 'axios';
import config, { debugLog } from '../config/environment';

// Create axios instance
const api = axios.create({
    baseURL: config.apiUrl,
    timeout: config.timeout,
});

// Log API configuration in development
debugLog('API Configuration:', {
    baseURL: config.apiUrl,
    timeout: config.timeout,
    environment: process.env.NODE_ENV
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (requestConfig) => {
        const token = localStorage.getItem('token');
        if (token) {
            requestConfig.headers.Authorization = `Bearer ${token}`;
        }

        debugLog('API Request:', {
            method: requestConfig.method?.toUpperCase(),
            url: requestConfig.url,
            baseURL: requestConfig.baseURL,
            hasAuth: !!token
        });

        return requestConfig;
    },
    (error) => {
        debugLog('API Request Error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
    (response) => {
        debugLog('API Response:', {
            status: response.status,
            url: response.config?.url,
            method: response.config?.method?.toUpperCase()
        });
        return response;
    },
    (error) => {
        const errorInfo = {
            status: error.response?.status,
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
            message: error.response?.data?.message || error.message
        };

        debugLog('API Response Error:', errorInfo);

        if (error.response?.status === 401) {
            // Only redirect if we're not already on the login page
            if (!window.location.pathname.includes('/login')) {
                // Token expired or invalid
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                // Use relative path for redirect to work on any domain
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    getProfile: () => api.get('/auth/profile'),
    changePassword: (passwordData) => api.patch('/auth/change-password', passwordData),
    createUser: (userData) => api.post('/auth/users', userData),
    getUsers: (params) => api.get('/auth/users', { params }),
    updateUser: (userId, userData) => api.put(`/auth/users/${userId}`, userData),
    deleteUser: (userId) => api.delete(`/auth/users/${userId}`),
    toggleUserStatus: (userId) => api.patch(`/auth/users/${userId}/toggle-status`),
    togglePiiProtection: (userId) => api.patch(`/auth/users/${userId}/toggle-pii`),
};

// Campaign API
export const campaignAPI = {
    // Dashboard and listing
    getDashboardStats: (params) => api.get('/campaigns/dashboard', { params }),
    getAdminDashboardStats: (params) => api.get('/campaigns/admin/dashboard', { params }),
    getCampaigns: (params) => api.get('/campaigns', { params }),
    getCampaignById: (id) => api.get(`/campaigns/${id}`),

    // Campaign CRUD operations
    createCampaign: (formData) => api.post('/campaigns', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    updateCampaign: (id, data) => api.put(`/campaigns/${id}`, data),
    updateCampaignWithDetails: (id, formData, config = {}) => api.put(`/campaigns/${id}/enhanced`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 minutes timeout for large operations
        ...config
    }),
    deleteCampaign: (id) => api.delete(`/campaigns/${id}`),

    // Campaign contacts/details
    getCampaignContacts: (id, params) => api.get(`/campaigns/${id}/contacts`, { params }),
    bulkDeleteContacts: (id, data) => api.delete(`/campaigns/${id}/contacts/bulk`, { data }),
    bulkStatusUpdate: (id, data) => api.put(`/campaigns/${id}/contacts/bulk-status-update`, data),
    bulkPulseUpdate: (id, data) => api.put(`/campaigns/${id}/contacts/bulk-pulse-update`, data),

    // Users for dropdown
    getUsers: () => api.get('/campaigns/users/list'),

    // User campaign creation
    createUserCampaign: (formData, config = {}) => api.post('/campaigns/user-create', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 1200000, // 20 minutes timeout for very large CSV uploads (1M+ records)
        ...config
    }),

    // Legacy endpoints (keeping for backwards compatibility)
    getCampaign: (id) => api.get(`/campaigns/${id}`),
    downloadCSV: (id, config = {}) => api.get(`/campaigns/${id}/download-csv`, {
        responseType: 'blob',
        timeout: 600000, // 10 minutes timeout for large downloads
        ...config
    }),
    downloadOriginalFile: (id, config = {}) => api.get(`/campaigns/${id}/download-original`, {
        responseType: 'blob',
        timeout: 600000, // 10 minutes timeout for large downloads
        ...config
    }),
    downloadPhoneNumbers: (id, config = {}) => api.get(`/campaigns/${id}/download-phone-numbers`, {
        responseType: 'blob',
        timeout: 600000, // 10 minutes timeout for large downloads
        ...config
    }),

    // New progress-based download endpoints
    startCSVDownload: (id) => api.post(`/campaigns/${id}/start-csv-download`),
    startPhoneNumbersDownload: (id) => api.post(`/campaigns/${id}/start-phone-download`),
    getDownloadProgress: (progressId) => api.get(`/campaigns/download-progress/${progressId}`),
    downloadGeneratedFile: (progressId) => api.get(`/campaigns/download-file/${progressId}`, {
        responseType: 'blob'
    }),
    uploadResult: (id, formData) => api.post(`/campaigns/${id}/upload-result`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    downloadResult: (id) => api.get(`/campaigns/${id}/download-result`, {
        responseType: 'blob'
    }),
    updateStatus: (id, data) => api.patch(`/campaigns/${id}/status`, data),
};

// Shorthand functions for convenience
export const login = (credentials) => authAPI.login(credentials);
export const getProfile = () => authAPI.getProfile();
export const createUser = (userData) => authAPI.createUser(userData);
export const getUsers = () => campaignAPI.getUsers();
export const getCampaigns = (params) => campaignAPI.getCampaigns(params);
export const createCampaign = (formData, config = {}) => api.post('/campaigns', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    ...config
});
export const getCampaignById = (id) => campaignAPI.getCampaignById(id);
export const updateCampaign = (id, data) => campaignAPI.updateCampaign(id, data);
export const deleteCampaign = (id) => campaignAPI.deleteCampaign(id);
export const getCampaignContacts = (id, params) => campaignAPI.getCampaignContacts(id, params);
export const getDashboardStats = () => campaignAPI.getDashboardStats();

export default api; 